import React from 'react';
import { Alert, Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Container } from '@mui/material';

// OHANA AI サービス本体のプライバシーポリシー(アプリ内)。
// 本ポリシーは本サイト(製品紹介ページ)のみを対象とするため、サービス利用者は
// こちらを参照する必要がある(ohana/aloha: docs/business/privacy-policy-draft.md 第2条4項と対応)。
const APP_PRIVACY_POLICY_URL = 'https://app.ohana-ai.jp/legal?doc=privacy-policy';
const CONTACT_EMAIL = 'info@cocowa-tech.com';

const PrivacyPolicyContent: React.FC = () => {
    return (
        <Container maxWidth="md" sx={{ p: 5 }}>
            <Typography variant="body1" paragraph>
                株式会社Cocowa（以下、「当社」といいます。）は、当社が運営するウェブサイト「OHANA AI」（https://ohana-ai.jp、以下、「本サイト」といいます。）における個人情報の取扱いについて、個人情報の保護に関する法律（以下、「個人情報保護法」といいます。）その他の関係法令を遵守し、以下の通りプライバシーポリシー（以下、「本ポリシー」といいます。）を定めます。
            </Typography>

            <Alert severity="info" sx={{ mb: 4 }}>
                <Typography variant="body2" paragraph sx={{ fontWeight: 700, mb: 1 }}>
                    OHANA AI をご利用中の事業所・職員の方へ
                </Typography>
                <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                    本ポリシーは、<strong>本サイト（製品紹介ページ）</strong>における個人情報の取扱いを定めるものです。
                    OHANA AI サービスをご利用中の事業所および職員の方の個人情報、ならびにサービスに入力される児童・利用者およびその保護者の個人情報の取扱いは、本ポリシーではなく、<strong>サービス内に掲載する「OHANA AI プライバシーポリシー」</strong>によります。
                </Typography>
                <Typography variant="body2" sx={{ mb: 0 }}>
                    <Link href={APP_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" underline="hover">
                        OHANA AI プライバシーポリシーを開く
                    </Link>
                    （サービスへのログイン後にご覧いただけます。ご契約前に内容をご確認されたい場合は、第12条の窓口までお申し付けください。）
                </Typography>
            </Alert>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第1条（事業者の名称および連絡先）
                </Typography>
                <ul>
                    <li>名称：株式会社Cocowa</li>
                    <li>所在地：〒530-0001 大阪府大阪市北区梅田1丁目2番2号 大阪駅前第2ビル12-12</li>
                    <li>代表者：代表取締役 中園耕輔</li>
                    <li>個人情報の取扱いに関する問合せ窓口：第12条に定めます。</li>
                </ul>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第2条（本ポリシーの適用範囲）
                </Typography>
                <Typography variant="body1" paragraph>
                    本ポリシーは、当社が本サイトを通じて取得する個人情報に適用されます。次の場面における個人情報の取扱いには、本ポリシーは適用されません。
                </Typography>
                <ul>
                    <li>OHANA AI サービスの提供に伴う個人情報の取扱い（サービス内に掲載する「OHANA AI プライバシーポリシー」によります。）</li>
                    <li>当社の営業活動、採用活動その他本サイト以外の場面で当社が取得する個人情報の取扱い</li>
                </ul>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第3条（取得する個人情報および取得方法）
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>本サイトには、お問い合わせフォームその他の入力フォームを設置していません。</strong>
                    当社が本サイトを通じて取得する個人情報は、本サイトに掲載するお問い合わせ用メールアドレス宛に利用者ご自身の操作でお送りいただいた電子メールに含まれる情報に限られます。具体的には、次の情報が含まれることがあります。
                </Typography>
                <ul>
                    <li>お名前、貴社・貴事業所の名称、部署名および役職</li>
                    <li>メールアドレス、電話番号その他ご連絡先として記載いただいた情報</li>
                    <li>お問い合わせ、資料請求、お見積りのご依頼等の内容</li>
                </ul>
                <Typography variant="body1" paragraph>
                    当社は、本サイトにおいて、氏名、住所、電話番号、銀行口座番号等を入力させる仕組みを設けておらず、これらを本サイト上で収集することはありません。
                </Typography>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第4条（利用目的）
                </Typography>
                <Typography variant="body1" paragraph>
                    当社は、前条により取得した個人情報を、次の目的で利用します。
                </Typography>
                <ul>
                    <li>お問い合わせ、資料請求、お見積りのご依頼等に対する回答および対応</li>
                    <li>OHANA AI サービスのご案内、ご提案および導入に関するご連絡</li>
                    <li>ご契約の締結およびその準備のための連絡</li>
                    <li>本サイトおよび当社サービスの改善のための分析（個人を特定しない統計的な形式に加工したうえで行います。）</li>
                    <li>法令に基づく対応</li>
                </ul>
                <Typography variant="body1" paragraph>
                    当社は、利用目的を変更する場合、変更前の目的と関連性を有すると合理的に認められる範囲内で行い、変更後の目的を本サイト上に掲載して通知します。
                </Typography>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第5条（第三者提供）
                </Typography>
                <Typography variant="body1" paragraph>
                    当社は、次の場合を除き、あらかじめご本人の同意を得ることなく、個人情報を第三者に提供しません。
                </Typography>
                <ul>
                    <li>法令に基づく場合</li>
                    <li>人の生命、身体または財産の保護のために必要がある場合であって、ご本人の同意を得ることが困難であるとき</li>
                    <li>国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、ご本人の同意を得ることにより当該事務の遂行に支障を及ぼすおそれがあるとき</li>
                    <li>合併その他の事由による事業の承継に伴って個人情報が提供される場合</li>
                </ul>
                <Typography variant="body1" paragraph>
                    なお、利用目的の達成に必要な範囲内で個人情報の取扱いを委託することは、第三者提供にあたりません。委託については次条に定めます。
                </Typography>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第6条（委託先および外国にある第三者への提供）
                </Typography>
                <Typography variant="body1" paragraph>
                    当社は、本サイトの運営およびお問い合わせへの対応にあたり、次の事業者に個人情報の取扱いを委託しています。いずれも外国に所在する事業者であるため、これらへの委託は個人情報の外国への移転にあたります。
                </Typography>
                <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>委託先</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>所在国</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>委託する業務</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>Amazon Web Services, Inc.（AWS）</TableCell>
                                <TableCell>米国（本サイトのデータの保存および配信は、日本国内の東京リージョンで行います。）</TableCell>
                                <TableCell>本サイトを配信するサーバーおよびファイル保存領域の提供</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Google LLC（Gmail）</TableCell>
                                <TableCell>米国（同社は、電子メールが保存されるデータセンターの所在国を特定して公表していません。）</TableCell>
                                <TableCell>お問い合わせ用電子メールの受信、保管および送信</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant="body1" paragraph>
                    <strong>移転先の外国における個人情報の保護に関する制度。</strong>
                    米国には、個人情報の保護に関する包括的な連邦法は存在せず、分野別の連邦法および州法（カリフォルニア州消費者プライバシー法（CCPA）等）により規律されています。詳細は、個人情報保護委員会「外国における個人情報の保護に関する制度等の調査（アメリカ合衆国）」（
                    <Link href="https://www.ppc.go.jp/enforcement/infoprovision/laws/offshore_report_america/" target="_blank" rel="noopener noreferrer" underline="hover">
                        https://www.ppc.go.jp/enforcement/infoprovision/laws/offshore_report_america/
                    </Link>
                    ）をご参照ください。
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>移転先の事業者が講ずる措置。</strong>
                    Amazon Web Services, Inc. および Google LLC は、いずれも ISO/IEC 27001、SOC 2 等の第三者認証を取得したデータセンターおよび管理体制のもとでサービスを提供しています。当社は、各社が定めるデータ処理に関する契約条件のもとでこれらを利用しています。
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>外国にある第三者への提供に関する当社の対応（個人情報保護法第28条第1項、同法施行規則第16条第1号）。</strong>
                    当社は、上記の事業者との間で、個人情報取扱事業者が講ずべき措置に相当する措置が継続的に講じられる体制を、データ処理に関する契約条件により確保したうえで個人情報を移転しています。当社は、当該措置の実施状況および当該外国の制度の有無・内容を、年1回以上、契約の履行状況の確認および個人情報保護委員会が公表する調査資料等の確認により定期的に確認します。相当措置の実施に支障が生じたときは必要かつ適切な措置を講じ、継続的な実施の確保が困難となったときは、当該事業者への提供を停止します。ご本人から求めがあった場合、当社は、同法施行規則第18条第3項に定める事項に関する情報を遅滞なく提供します。第12条の窓口までお申し出ください。
                </Typography>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第7条（安全管理措置）
                </Typography>
                <Typography variant="body1" paragraph>
                    当社は、個人情報の漏えい、滅失または毀損の防止その他の安全管理のため、次の措置を講じています。
                </Typography>
                <ul>
                    <li>
                        <strong>組織的措置。</strong>
                        当社は、代表取締役を個人情報の取扱いに関する責任者と定め、個人情報の取扱状況を把握するとともに、本ポリシーおよび当社の個人情報取扱手順を定期的に見直します。漏えい等の事案が発生した場合の報告連絡体制を整備しています。
                    </li>
                    <li>
                        <strong>人的措置。</strong>
                        当社の役員および従業者に対し、個人情報の取扱いに関する秘密保持義務を課し、必要な教育を行います。
                    </li>
                    <li>
                        <strong>物理的措置。</strong>
                        本サイトの配信基盤およびお問い合わせ用電子メールは、第三者認証を取得したクラウド事業者のデータセンターに設置された設備を利用しており、当社が物理的な記録媒体で個人情報を保管することはありません。
                    </li>
                    <li>
                        <strong>技術的措置。</strong>
                        本サイトとブラウザとの通信を暗号化（TLS）しています。お問い合わせ用電子メールを取り扱う当社の端末およびアカウントには、認証によるアクセス制限を設けています。
                    </li>
                    <li>
                        <strong>外的環境の把握。</strong>
                        当社は、外国において個人情報を取り扱うにあたり、当該外国における個人情報の保護に関する制度を把握したうえで、安全管理措置を講じています。個人情報を取り扱う外国の名称は次のとおりです。
                        <ul>
                            <li>
                                <strong>クラウドサービスを提供する事業者が所在する外国：アメリカ合衆国</strong>
                                （Amazon Web Services, Inc.、Google LLC）
                            </li>
                            <li>
                                <strong>個人情報が保存されるサーバーが所在する国：</strong>
                                本サイトの配信基盤（Amazon Web Services, Inc.）については<strong>日本</strong>（東京リージョン）です。お問い合わせ用電子メール（Google LLC の Gmail）については、同社が電子メールの保存先データセンターの所在国を特定して公表しておらず、当社が相当の努力を払っても特定することができません。このため当社は、本サイトを通じて取得する情報を第3条に記載した範囲に限定するとともに、お問い合わせへの対応の完了後は第9条の定めにより取り扱います。
                            </li>
                        </ul>
                        米国における個人情報の保護に関する制度の概要は、第6条に記載のとおりです。
                    </li>
                </ul>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第8条（Cookie等の利用および外部送信）
                </Typography>
                <ul>
                    <li>
                        当社は、本サイトにおいて、<strong>アクセス解析ツール、広告配信ツールその他の行動追跡技術を利用していません。</strong>
                        本サイトの閲覧のみによって、利用者を識別する情報が当社または第三者に送信されることはありません。
                    </li>
                    <li>
                        当社は、本サイトにおいて、利用者を識別する目的で Cookie を利用していません。本サイトは、画面表示の設定を保持する目的でブラウザの保存領域を利用することがありますが、これらの情報は利用者のブラウザ内に保持され、当社が収集するものではありません。
                    </li>
                    <li>
                        本サイトに掲載する外部サイトへのリンクをたどられた場合、リンク先における個人情報の取扱いは、当該サイトの運営者が定める方針によります。当社は責任を負いかねます。
                    </li>
                </ul>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第9条（保存期間）
                </Typography>
                <Typography variant="body1" paragraph>
                    当社は、お問い合わせに関する電子メールおよびその記録を、お問い合わせへの対応の完了後、当社との取引の履歴および将来のお問い合わせへの対応に必要な期間（原則として3年間）保管し、その後、順次消去します。ただし、ご契約に至った場合の情報の取扱いは、当該ご契約に関する定めによります。また、法令上保存を要する場合は、当該法令の定める期間保管します。
                </Typography>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第10条（開示、訂正、利用停止等の請求）
                </Typography>
                <Typography variant="body1" paragraph>
                    ご本人は、当社に対し、個人情報保護法の定めに従い、自己の保有個人データについて、利用目的の通知、開示、内容の訂正・追加・削除、利用の停止・消去、第三者提供の停止、および第三者提供記録の開示を請求することができます。請求は第12条の窓口までお申し出ください。当社は、請求者がご本人であることを確認したうえで、法令に定める期間内に対応します。
                </Typography>
                <Typography variant="body1" paragraph>
                    法令の定めにより開示等に応じないことができる場合、当社は、その旨および理由をご本人に通知します。開示および第三者提供記録の開示の請求について、手数料はいただきません。ただし、電子メール以外の方法（書面の郵送等）による回答をご希望される場合、郵送に要する実費をご負担いただくことがあります。
                </Typography>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第11条（本ポリシーの変更）
                </Typography>
                <Typography variant="body1" paragraph>
                    当社は、法令の変更または本サイトの運営内容の変更に応じ、本ポリシーを変更することがあります。変更後の本ポリシーは、本サイト上に掲載した時点から効力を生じます。重要な変更を行う場合、当社は、本サイト上でその旨を分かりやすくお知らせします。
                </Typography>
            </Box>

            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    第12条（お問い合わせ窓口）
                </Typography>
                <Typography variant="body1" paragraph>
                    本ポリシーに関するご質問、ご意見、第10条の請求、および個人情報の取扱いに関するご連絡は、次の窓口までお申し出ください。
                </Typography>
                <ul>
                    <li>株式会社Cocowa 個人情報お問い合わせ窓口</li>
                    <li>所在地：〒530-0001 大阪府大阪市北区梅田1丁目2番2号 大阪駅前第2ビル12-12</li>
                    <li>
                        メールアドレス：
                        <Link href={`mailto:${CONTACT_EMAIL}`} underline="hover">
                            {CONTACT_EMAIL}
                        </Link>
                    </li>
                    <li>受付方法：電子メールのみ（電話および来訪による受付は行っていません。）</li>
                    <li>対応時間：平日10時から17時まで（土日祝日および当社の休業日を除きます。）お問い合わせから原則として3営業日以内に一次回答します。</li>
                </ul>
            </Box>

            <Box mb={4}>
                <Typography variant="body2" color="text.secondary">
                    制定日：2026年9月10日
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    株式会社Cocowa
                </Typography>
            </Box>
        </Container>
    );
};

export default PrivacyPolicyContent;
